if (!customElements.get('mini-bundle-selector')) {
  const miniBundleGroupCounters = new Map();

  const slugifyForBundleId = (value) =>
    String(value || 'product')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'product';

  const nextMiniBundleGroupId = (productKey, qty) => {
    const counterKey = `${productKey}-${qty}`;
    const nextCount = (miniBundleGroupCounters.get(counterKey) || 0) + 1;
    miniBundleGroupCounters.set(counterKey, nextCount);
    return `${productKey}-${qty}x-${nextCount}`;
  };

  class MiniBundleSelector extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.miniProductId = this.dataset.miniProductId;
      this.productInfoContainer = this.closest('.product__info-container');
      
      this.targetQuantity = 1;
      this.selected = [];
      this.rowSelections = [];
      this.baseLabelHtml = '';
      
      this.selectors = {
        qtyBlocks: '.mini-bundle__qty-block',
        shadeRows: '.mini-bundle__shade-rows',
        selectedShadesContainer: '.mini-bundle__selected-shades',
        slotsHint: '.mini-bundle__slots-hint',
        selectMore: '.mini-bundle__select-more',
        swatches: '.mini-bundle__swatches',
        swatch: '.mini-bundle__swatch',
        atcBtn: '.mini-bundle__atc-btn',
        atcText: '.mini-bundle__atc-text',
        atcPrice: '.mini-bundle__atc-price',
        buyNowWrap: '[data-mini-bundle-buy-now]',
        paymentVariantId: '.mini-bundle__payment-variant-id',
        paymentQuantity: '.mini-bundle__payment-quantity',
        spinner: '.loading__spinner'
      };
    }

    connectedCallback() {
      this.initEvents();

      const initialQtyBlock = this.querySelector(`${this.selectors.qtyBlocks}.active`) || this.querySelector(this.selectors.qtyBlocks);
      if (initialQtyBlock) this.handleQtyBlockSelect(initialQtyBlock);
      else this.updateUI();
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    titleCase(value) {
      return String(value || '')
        .split(' ')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    }

    extractSwatchColorFromStyle(styleText) {
      const style = String(styleText || '');
      const bg = style.match(/background\s*:\s*([^;]+);?/i);
      if (bg) return bg[1].trim();
      const bgc = style.match(/background-color\s*:\s*([^;]+);?/i);
      if (bgc) return bgc[1].trim();
      return '#1a1410';
    }

    formatVariantTitleHtml(title, swatchStyle) {
      const raw = String(title || '').trim();
      if (!raw) return '';
      let leftPart = raw;
      let rightPart = '';

      if (raw.includes(' - ')) {
        const parts = raw.split(' - ');
        leftPart = parts.shift() || '';
        rightPart = parts.join(' - ') || '';
      } else if (raw.includes('—')) {
        const parts = raw.split('—');
        leftPart = parts.shift() || '';
        rightPart = parts.join('—') || '';
      }

      const left = String(leftPart || '').trim();
      const right = String(rightPart || '').trim();
      const leftHtml = `<strong>${this.escapeHtml(left)}</strong>`;
      if (!right) return leftHtml;
      return `${leftHtml} - ${this.escapeHtml(right)}`;
    }

    initEvents() {
      // Quantity Blocks
      this.querySelectorAll(this.selectors.qtyBlocks).forEach(block => {
        block.addEventListener('click', () => this.handleQtyBlockSelect(block));
      });

      const shadeRows = this.querySelector(this.selectors.shadeRows);
      shadeRows?.addEventListener('click', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        const row = btn.closest('.mini-bundle__shade-row');
        if (!row) return;
        const rowIndex = parseInt(row.dataset.rowIndex || '0', 10) || 0;
        e.preventDefault();
        e.stopPropagation();
        this.handleRowSwatchClick(rowIndex, btn);
      });

      shadeRows?.addEventListener('mouseover', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        const row = btn.closest('.mini-bundle__shade-row');
        if (!row) return;
        const rowIndex = parseInt(row.dataset.rowIndex || '0', 10) || 0;
        btn.classList.add('hover-peek');
        const circle = btn.querySelector('.mini-bundle__swatch-circle');
        const swatchStyle = circle?.getAttribute('style') || '';
        this.setRowHoverLabel(rowIndex, this.formatVariantTitleHtml(btn.dataset.variantTitle || '', swatchStyle));
      });

      shadeRows?.addEventListener('mouseout', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        const related = e.relatedTarget;
        if (related && btn.contains(related)) return;
        const row = btn.closest('.mini-bundle__shade-row');
        if (!row) return;
        const rowIndex = parseInt(row.dataset.rowIndex || '0', 10) || 0;
        btn.classList.remove('hover-peek');
        this.clearRowHoverLabel(rowIndex);
      });

      const swatchesWrap = this.querySelector(this.selectors.swatches);
      swatchesWrap?.addEventListener('click', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.handleSwatchClick(btn);
      });

      swatchesWrap?.addEventListener('mouseover', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        btn.classList.add('hover-peek');
        const circle = btn.querySelector('.mini-bundle__swatch-circle');
        const swatchStyle = circle?.getAttribute('style') || '';
        this.setHoverLabel(this.formatVariantTitleHtml(btn.dataset.variantTitle || '', swatchStyle));
      });

      swatchesWrap?.addEventListener('mouseout', (e) => {
        const btn = e.target.closest(this.selectors.swatch);
        if (!btn) return;
        const related = e.relatedTarget;
        if (related && btn.contains(related)) return;
        btn.classList.remove('hover-peek');
        this.clearHoverLabel();
      });

      const slotsWrap = this.querySelector(this.selectors.selectedShadesContainer);
      slotsWrap?.addEventListener('click', (e) => {
        const slotBtn = e.target.closest('.mini-bundle__slot');
        if (!slotBtn) return;
        const variantId = slotBtn.dataset.variantId;
        if (!variantId) return;
        this.selected = this.selected.filter((s) => String(s.variantId) !== String(variantId));
        this.updateUI();
      });

      swatchesWrap?.addEventListener(
        'touchmove',
        (e) => {
          const t = e.touches?.[0];
          if (!t) return;
          const el = document.elementFromPoint(t.clientX, t.clientY);
          const btn = el && el.closest && el.closest(this.selectors.swatch);
          if (!btn) return;
          const circle = btn.querySelector('.mini-bundle__swatch-circle');
          const swatchStyle = circle?.getAttribute('style') || '';
          this.setHoverLabel(this.formatVariantTitleHtml(btn.dataset.variantTitle || '', swatchStyle));
        },
        { passive: true }
      );

      swatchesWrap?.addEventListener(
        'touchend',
        () => {
          if (this.selected.length > 0) {
            this.setHoverLabel(this.selected[this.selected.length - 1].titleHtml);
            return;
          }
          this.clearHoverLabel();
        },
        { passive: true }
      );

      // Add to Cart
      this.querySelector(this.selectors.atcBtn)?.addEventListener('click', () => this.handleAddToCart());
    }

    getTemplateSwatches() {
      const wrap = this.querySelector(this.selectors.swatches);
      if (!wrap) return [];
      return Array.from(wrap.querySelectorAll(this.selectors.swatch));
    }

    selectionFromButton(btn) {
      const variantId = btn?.dataset?.variantId;
      const title = btn?.dataset?.variantTitle || '';
      const galleryFilterValue = btn?.dataset?.variantGalleryValue || title;
      const circle = btn?.querySelector?.('.mini-bundle__swatch-circle');
      const swatchStyle = circle?.getAttribute('style') || '';
      const available = btn?.dataset?.available !== 'false';
      return {
        variantId,
        title,
        galleryFilterValue,
        titleHtml: this.formatVariantTitleHtml(title, swatchStyle),
        swatchStyle,
        available,
      };
    }

    syncGalleryFromPrimarySelection() {
      const primarySelection = this.rowSelections?.[0] || this.selected?.[0];
      const productInfo = this.closest('product-info');
      const mediaGallery = productInfo?.querySelector('media-gallery, media-gallery-new');
      if (!primarySelection || !mediaGallery?.applyVariantAltFilter) return;

      mediaGallery.applyVariantAltFilter(primarySelection.galleryFilterValue || primarySelection.title || '');
    }

    handleQtyBlockSelect(block) {
      const qty = parseInt(block.dataset.qty);
      this.targetQuantity = qty;
      
      this.querySelectorAll(this.selectors.qtyBlocks).forEach(b => b.classList.remove('active'));
      block.classList.add('active');

      this.syncProductFormVisibility();

      this.selected = [];
      this.rowSelections = new Array(Math.max(1, this.targetQuantity)).fill(null);
      this.baseLabelHtml = '';

      this.ensureShadeRowsBuilt();
      this.preselectDefaultsForRows();

      const atcPriceText = block.dataset.atcPrice;
      const atcPrice = this.querySelector(this.selectors.atcPrice);
      if (atcPrice) {
        atcPrice.textContent = atcPriceText ? `${atcPriceText}` : '';
      }

      this.updateUI();
    }

    preselectDefaultsForRows() {
      const templateSwatches =
        this.getTemplateSwatches().filter((b) => b.dataset.available !== 'false') || [];
      const first = templateSwatches[0] || this.getTemplateSwatches()[0];
      if (!first) return;
      const selection = this.selectionFromButton(first);
      this.rowSelections = new Array(Math.max(1, this.targetQuantity)).fill(null).map(() => ({ ...selection }));
      this.selected = this.rowSelections.filter(Boolean);
    }

    selectDefaultShade() {
      const first = this.getTemplateSwatches().find((b) => b.dataset.available !== 'false') || this.getTemplateSwatches()[0];
      if (!first) return;
      const selection = this.selectionFromButton(first);
      this.rowSelections = [selection];
      this.selected = [selection];
    }

    syncProductFormVisibility() {
      if (!this.productInfoContainer) return;

      if (this.miniProductId) this.productInfoContainer.classList.add('mini-bundle-multi-active');
    }

    handleSwatchClick(btn) {
      if (this.targetQuantity > 1) return;
      const isAvailable = btn.dataset.available !== 'false';
      if (!isAvailable) return;

      const selection = this.selectionFromButton(btn);
      this.rowSelections = [selection];
      this.selected = [selection];
      this.updateUI();
    }

    handleRowSwatchClick(rowIndex, btn) {
      const isAvailable = btn.dataset.available !== 'false';
      if (!isAvailable && this.targetQuantity > 1) return;

      const variantId = btn.dataset.variantId;
      const title = btn.dataset.variantTitle || '';
      const circle = btn.querySelector('.mini-bundle__swatch-circle');
      const swatchStyle = circle?.getAttribute('style') || '';

      const selection = {
        variantId,
        title,
        titleHtml: this.formatVariantTitleHtml(title, swatchStyle),
        swatchStyle,
        available: isAvailable,
      };

      const safeIndex = Math.max(0, Math.min(this.targetQuantity - 1, rowIndex));
      if (!Array.isArray(this.rowSelections) || this.rowSelections.length !== this.targetQuantity) {
        this.rowSelections = new Array(Math.max(1, this.targetQuantity)).fill(null);
      }
      this.rowSelections[safeIndex] = selection;
      this.selected = this.rowSelections.filter(Boolean);
      this.updateUI();
    }

    setHoverLabel(text) {
      const selectMore = this.querySelector(this.selectors.selectMore);
      if (!selectMore) return;
      if (!text) return;
      selectMore.innerHTML = text;
    }

    clearHoverLabel() {
      const selectMore = this.querySelector(this.selectors.selectMore);
      if (!selectMore) return;
      selectMore.innerHTML = this.baseLabelHtml || '';
    }

    setRowHoverLabel(rowIndex, text) {
      const rows = this.querySelector(this.selectors.shadeRows);
      const row = rows?.querySelector(`.mini-bundle__shade-row[data-row-index="${rowIndex}"]`);
      const value = row?.querySelector('.mini-bundle__shade-value');
      if (!value || !text) return;
      value.innerHTML = text;
      row.dataset.isHovering = 'true';
    }

    clearRowHoverLabel(rowIndex) {
      const rows = this.querySelector(this.selectors.shadeRows);
      const row = rows?.querySelector(`.mini-bundle__shade-row[data-row-index="${rowIndex}"]`);
      if (!row) return;
      row.dataset.isHovering = 'false';
      const value = row.querySelector('.mini-bundle__shade-value');
      if (!value) return;
      const sel = this.rowSelections?.[rowIndex];
      value.innerHTML = sel?.titleHtml || '&nbsp;';
    }

    updateUI() {
      this.updateSelectedShadesUI();
      this.updateShadeListUI();
      this.updateATCState();
      this.syncGalleryFromPrimarySelection();
    }

    updateSelectedShadesUI() {
      this.ensureShadeRowsBuilt();

      if (!Array.isArray(this.rowSelections) || this.rowSelections.length !== this.targetQuantity) {
        this.rowSelections = new Array(Math.max(1, this.targetQuantity)).fill(null);
      }
      if (this.targetQuantity === 1 && !this.rowSelections[0] && this.selected[0]) {
        this.rowSelections[0] = this.selected[0];
      }
      this.selected = this.rowSelections.filter(Boolean);

      const shadeRows = this.querySelector(this.selectors.shadeRows);
      if (shadeRows) {
        const rows = Array.from(shadeRows.querySelectorAll('.mini-bundle__shade-row'));
        rows.forEach((row, i) => {
          const prefix = row.querySelector('.mini-bundle__shade-prefix');
          const value = row.querySelector('.mini-bundle__shade-value');
          if (prefix) {
            prefix.textContent = this.targetQuantity === 1 ? '' : `${i + 1}. Farbe:`;
          }
          const sel = this.rowSelections[i];
          if (value && row.dataset.isHovering !== 'true') {
            value.innerHTML = sel?.titleHtml || '&nbsp;';
          }
          row.classList.toggle('show', i < this.targetQuantity);
        });

        rows.forEach((row, i) => {
          const selectedVariantId = String(this.rowSelections[i]?.variantId || '');
          row.querySelectorAll(this.selectors.swatch).forEach((btn) => {
            const isActive = selectedVariantId && String(btn.dataset.variantId) === selectedVariantId;
            btn.classList.toggle('active', isActive);
            const isAvailable = btn.dataset.available !== 'false';
            btn.disabled = !isAvailable && this.targetQuantity > 1;
          });
        });
      }

      const container = this.querySelector(this.selectors.selectedShadesContainer);
      if (!container) return;

      container.innerHTML = '';

      const filledCount = this.selected.length;
      const slotsRow = container.closest('.mini-bundle__slots-row');
      if (slotsRow) slotsRow.style.display = this.targetQuantity > 1 ? '' : 'none';

      if (this.targetQuantity === 1) {
        const hint = this.querySelector(this.selectors.slotsHint);
        if (hint) hint.textContent = '';
      }

      for (let i = 0; i < this.targetQuantity; i++) {
        const slot = document.createElement('div');
        slot.className = 'mini-bundle__slot';
        const sel = this.selected[i];
        if (sel) {
          slot.classList.add('filled');
          slot.dataset.variantId = String(sel.variantId);
          slot.style.border = 'none';
        }

        const inner = document.createElement('span');
        inner.className = 'mini-bundle__slot-inner';
        if (sel?.swatchStyle) inner.setAttribute('style', sel.swatchStyle);
        slot.appendChild(inner);

        if (sel) {
          const remove = document.createElement('span');
          remove.className = 'mini-bundle__slot-remove';
          remove.innerHTML = '&times;';
          slot.appendChild(remove);
        }

        container.appendChild(slot);
      }

      const hint = this.querySelector(this.selectors.slotsHint);
      if (hint) hint.textContent = this.targetQuantity > 1 ? `${filledCount}/${this.targetQuantity} ausgewählt` : '';

      const selectMore = this.querySelector(this.selectors.selectMore);
      if (selectMore) {
        if (this.targetQuantity === 1) {
          this.baseLabelHtml = this.selected[0]?.titleHtml || '&nbsp;';
          selectMore.innerHTML = this.baseLabelHtml;
          return;
        }

        const remaining = Math.max(0, this.targetQuantity - filledCount);
        if (remaining > 0) {
this.baseLabelHtml = `<span style="color:#bbb;font-weight:300">Wähle noch ${remaining} ${remaining === 1 ? 'Farbe' : 'Farben'} aus.</span>`;
        } else {
          const last = this.selected[this.selected.length - 1];
          this.baseLabelHtml = last?.titleHtml || '';
        }
        selectMore.innerHTML = this.baseLabelHtml;
      }
    }

    updateShadeListUI() {
      const templateWrap = this.querySelector(this.selectors.swatches);
      if (!templateWrap) return;

      const selectedIds = new Set(this.selected.map((s) => String(s.variantId)));
      templateWrap.querySelectorAll(this.selectors.swatch).forEach((btn) => {
        const isSelected = selectedIds.has(String(btn.dataset.variantId));
        btn.classList.toggle('confirmed', isSelected && this.targetQuantity > 1);
        btn.classList.toggle('confirmed-simple', isSelected && this.targetQuantity === 1);
        const isAvailable = btn.dataset.available !== 'false';
        btn.disabled = !isAvailable && this.targetQuantity > 1;
      });
    }

    ensureShadeRowsBuilt() {
      const container = this.querySelector(this.selectors.shadeRows);
      const templateWrap = this.querySelector(this.selectors.swatches);
      if (!container || !templateWrap) return;

      const templateButtons = this.getTemplateSwatches();
      if (!templateButtons.length) return;

      const existingCount = container.querySelectorAll('.mini-bundle__shade-row').length;
      if (existingCount === this.targetQuantity) return;

      container.innerHTML = '';
      for (let i = 0; i < this.targetQuantity; i++) {
        const row = document.createElement('div');
        row.className = 'mini-bundle__shade-row shade-row';
        row.dataset.rowIndex = String(i);

        const label = document.createElement('p');
        label.className = 'mini-bundle__shade-label';

        const prefix = document.createElement('span');
        prefix.className = 'mini-bundle__shade-prefix';
        prefix.textContent = this.targetQuantity === 1 ? '' : `${i + 1}. Farbe:`;

        const value = document.createElement('span');
        value.className = 'mini-bundle__shade-value';
        value.innerHTML = '&nbsp;';

        label.appendChild(prefix);
        label.appendChild(value);

        const swatches = document.createElement('div');
        swatches.className = 'mini-bundle__shade-swatches';

        templateButtons.forEach((btn) => {
          const clone = btn.cloneNode(true);
          clone.classList.remove('active', 'confirmed', 'confirmed-simple', 'hover-peek');
          swatches.appendChild(clone);
        });

        row.appendChild(label);
        row.appendChild(swatches);
        container.appendChild(row);
      }

      requestAnimationFrame(() => {
        container.querySelectorAll('.mini-bundle__shade-row').forEach((row) => row.classList.add('show'));
      });
    }

    updateATCState() {
      const btn = this.querySelector(this.selectors.atcBtn);
      const isComplete = this.selected.length === this.targetQuantity && this.selected.every((s) => s && s.available);
      if (btn) btn.disabled = !isComplete;
      this.updateBuyNowForm(isComplete);
    }

    updateBuyNowForm(isComplete) {
      const wrap = this.querySelector(this.selectors.buyNowWrap);
      if (!wrap) return;

      wrap.toggleAttribute('disabled', !isComplete);
      wrap.classList.toggle('mini-bundle__buy-now-btn--disabled', !isComplete);

      const primarySelection = this.rowSelections?.[0] || this.selected?.[0];
      const idInput = wrap
        .closest('form')
        ?.querySelector(this.selectors.paymentVariantId);
      const qtyInput = wrap
        .closest('form')
        ?.querySelector(this.selectors.paymentQuantity);

      if (idInput && primarySelection?.variantId && idInput.value !== String(primarySelection.variantId)) {
        idInput.value = primarySelection.variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (qtyInput && String(this.targetQuantity) !== qtyInput.value) {
        qtyInput.value = String(this.targetQuantity);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    async handleAddToCart() {
      const btn = this.querySelector(this.selectors.atcBtn);
      const spinner = this.querySelector(this.selectors.spinner);
      const atcText = this.querySelector(this.selectors.atcText);
      const atcPrice = this.querySelector(this.selectors.atcPrice);

      if (!btn || btn.disabled) return;

      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      spinner?.classList.remove('hidden');
      atcText?.classList.add('hidden');
      atcPrice?.classList.add('hidden');

      try {
        const isBundle = this.targetQuantity > 1;
        const bundleProductTitle = (this.dataset.bundleProductTitle || '').trim();
        const bundleProductKey = slugifyForBundleId(this.miniProductId || bundleProductTitle);
        const bundleId = isBundle
          ? nextMiniBundleGroupId(bundleProductKey, this.targetQuantity)
          : null;
        const activeQtyBlock = this.querySelector(`${this.selectors.qtyBlocks}.active`);
        const bundleSetTitle = isBundle ? (activeQtyBlock?.dataset.setTitle || `SET OF ${this.targetQuantity}`) : '';
        const bundleSize = isBundle ? this.targetQuantity : 0;
        const bundleDiscountCentsPerItem = isBundle ? parseInt(activeQtyBlock?.dataset.discountCentsPerItem || '0', 10) || 0 : 0;

        const items = this.selected.filter(Boolean).reduce((acc, shade) => {
          const existing = acc.find(i => i.id === shade.variantId);
          if (existing) {
            existing.quantity++;
          } else {
            acc.push({ id: shade.variantId, quantity: 1 });
          }
          return acc;
        }, []);

        // if (window.RevyBundle && window.RevyBundle.api && window.RevyBundle.api.setGroup) {
        //   console.log("RevyBundleGroup: Setting RevyBundle group");
        //   const groupIds = items.flatMap((item) => Array(item.quantity).fill(item.id));
        //   if (groupIds.length >= 2) {
        //     const saved = await window.RevyBundle.api.setGroup(...groupIds);
        //     console.log("RevyBundleGroup: Set:", saved);
        //     console.log(window.RevyBundle.api.getBundleInfo());
        //   }
        // }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const addUrl =
          typeof routes !== 'undefined' && routes.cart_add_url
            ? routes.cart_add_url
            : window.Shopify?.routes?.root
              ? `${window.Shopify.routes.root}cart/add.js`
              : '/cart/add.js';

        const getConfig = () => {
          if (typeof fetchConfig === 'function') return fetchConfig('javascript');
          return { method: 'POST', headers: { Accept: 'application/json' } };
        };

        let lastResponseJson = null;
        for (const item of items) {
          const config = getConfig();
          config.headers = config.headers || {};
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', item.id);
          formData.append('quantity', item.quantity);
          if (isBundle && bundleId) {
            formData.append('properties[_bundle_id]', bundleId);
            formData.append('properties[_bundle_product_title]', bundleProductTitle);
            formData.append('properties[_bundle_set_title]', bundleSetTitle);
            formData.append('properties[_bundle_size]', String(bundleSize));
            formData.append('properties[_bundle_discount_cents_per_item]', String(bundleDiscountCentsPerItem));
          }

          if (cart && typeof cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
          }

          config.body = formData;

          const response = await fetch(addUrl, config);
          lastResponseJson = await response.json();

          if (lastResponseJson?.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'mini-bundle',
                productVariantId: item.id,
                errors: lastResponseJson.errors || lastResponseJson.description,
                message: lastResponseJson.message,
              });
            }
            throw new Error(lastResponseJson.description || 'Failed to add to cart');
          }
        }

        if (!lastResponseJson) throw new Error('Failed to add to cart');

        if (cart && typeof cart.renderContents === 'function' && lastResponseJson.sections) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-bundle', cartData: lastResponseJson });
          }
          cart.renderContents(lastResponseJson);
        } else if (document.querySelector('cart-drawer')) {
          const res = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`);
          const sections = await res.json();
          document.querySelector('cart-drawer')?.renderContents({ sections, item_count: 1 });
        }

      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        alert('There was an error adding items to the cart. Please try again.');
      } finally {
        btn.classList.remove('loading');
        spinner?.classList.add('hidden');
        atcText?.classList.remove('hidden');
        atcPrice?.classList.remove('hidden');
        btn.removeAttribute('aria-disabled');
        this.updateATCState();
      }
    }

    disconnectedCallback() {
      this.productInfoContainer?.classList.remove('mini-bundle-multi-active');
    }
  }

  customElements.define('mini-bundle-selector', MiniBundleSelector);
}
